/**
 * USAGE — each allowance, its refresh date and any add-on balance (Stage 2, Founder-authorized).
 * Add-ons are explicit one-time purchases for Essential and Premium, used only after the allowance.
 * Web buys through Stripe Checkout; iPhone buys through the App Store (StoreKit), verified by the server.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { isIosApp } from '../utils/iosPurchaseGate';
import { fetchApplePrices, purchaseApplePlan } from '../utils/iosBilling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    title: 'Usage', resets: (d) => `Refreshes on ${d}.`, trialEnds: (d) => `Your trial ends on ${d}.`, unlimited: 'Not limited',
    monthly: (left, total) => `${left} of ${total} left this month`,
    trial: (left, total) => `${left} of ${total} left in your 7-day trial`,
    extra: (n) => `plus ${n} from add-ons (never expire)`,
    message: 'Messages', listen: 'Listen', talk: 'Live Talk', dream: 'Dreams with Reflection',
    image_creation: 'Images', shared_audio: 'Shared audio', min: 'min',
    addons: 'Add more', addonNote: 'One-time purchase, used only after your monthly allowance. It never expires and is never charged automatically. If your membership ends, unused add-ons are kept and can be used again whenever you have an active Essential or Premium membership.',
    buy: 'Buy', ios: 'Add-ons will be available in the App Store soon.', fail: 'The purchase could not start. Nothing was charged.',
    added: 'Your add-on is ready.',
    low: (what) => `You've used most of this month's ${what}.`,
    lowMore: ' If you would like more before then, add-ons are below.',
    nouns: { message: 'Messages', listen: 'Listen minutes', talk: 'Live Talk minutes', dream: 'Dreams', image_creation: 'images', shared_audio: 'audio recordings' },
  },
  es: {
    title: 'Uso', resets: (d) => `Se renueva el ${d}.`, trialEnds: (d) => `Tu prueba termina el ${d}.`, unlimited: 'Sin límite',
    monthly: (left, total) => `${left} de ${total} restantes este mes`,
    trial: (left, total) => `${left} de ${total} restantes en tu prueba de 7 días`,
    extra: (n) => `más ${n} de complementos (nunca caducan)`,
    message: 'Mensajes', listen: 'Escuchar', talk: 'Talk en vivo', dream: 'Sueños con Reflexión',
    image_creation: 'Imágenes', shared_audio: 'Audio compartido', min: 'min',
    addons: 'Añadir más', addonNote: 'Compra única, usada solo después de tu cantidad mensual. Nunca caduca y nunca se cobra automáticamente. Si tu membresía termina, los complementos no usados se conservan y podrás volver a usarlos cuando tengas una membresía Essential o Premium activa.',
    buy: 'Comprar', ios: 'Los complementos estarán disponibles pronto en la App Store.', fail: 'No se pudo iniciar la compra. No se ha cobrado nada.',
    added: 'Tu complemento está listo.',
    low: (what) => `Has usado la mayor parte de tus ${what} de este mes.`,
    lowMore: ' Si quieres más antes, tienes complementos abajo.',
    nouns: { message: 'Mensajes', listen: 'minutos de Escuchar', talk: 'minutos de Talk en vivo', dream: 'Sueños', image_creation: 'imágenes', shared_audio: 'grabaciones de audio' },
  },
};

const ROWS = ['message', 'listen', 'talk', 'dream', 'image_creation', 'shared_audio'];
const LOW_SHARE = 0.8;  // one neutral notice at about 80% of a monthly allowance
const SECONDS = { listen: true, talk: true };

export const MembershipUsage = ({ lang }) => {
  const es = lang === 'es';
  const c = COPY[es ? 'es' : 'en'];
  const ios = isIosApp();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [applePrices, setApplePrices] = useState({});
  const [buying, setBuying] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/usage/allowances`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/addons`).then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    const ids = ios ? (catalog?.items || []).map((a) => a.apple_product_id).filter(Boolean) : [];
    if (ids.length) fetchApplePrices(ids).then(setApplePrices).catch(() => {});
  }, [ios, catalog]);

  if (!data) return null;
  const when = data.period_end
    ? new Date(data.period_end).toLocaleDateString(es ? 'es-ES' : 'en-US', { month: 'long', day: 'numeric' })
    : null;
  const show = (k, v) => (SECONDS[k] ? `${Math.floor(v / 60)} ${c.min}` : v);
  const left = data.plan === 'trial' ? c.trial : c.monthly;

  const buyWeb = async (addonId) => {
    setBuying(addonId);
    try {
      const { data: r } = await axios.post(`${API}/addons/checkout`, { addon_id: addonId, origin_url: window.location.origin });
      if (r?.url) window.location.href = r.url;
    } catch {
      alert(c.fail);
    } finally {
      setBuying(null);
    }
  };

  const buyApple = async (a) => {
    setBuying(a.id);
    try {
      const r = await purchaseApplePlan({
        productId: a.apple_product_id, appAccountToken: catalog.app_account_token,
        verify: async (signed) => (await axios.post(`${API}/addons/apple/verify`, { signed_transaction: signed })).data,
      });
      if (r?.credited) { alert(c.added); load(); }
    } catch {
      alert(c.fail);
    } finally {
      setBuying(null);
    }
  };

  const items = (catalog?.items || []).filter((a) => !ios || a.apple_product_id);
  // The single low-allowance notice: the first monthly allowance at 80% or more, not yet spent.
  const low = data.unlimited ? null : ROWS.find((k) => {
    const row = data[k];
    const base = row?.plan_allowance ?? row?.allowance;
    return row && base > 0 && row.used >= LOW_SHARE * base && row.used < base;
  });
  const price = (a) => (ios ? applePrices[a.apple_product_id] : null) || `$${a.price.toFixed(2)}`;

  return (
    <div className="sub__card" data-testid="membership-usage">
      <p className="sub__tier">{c.title}</p>
      {when && <p className="sub__note" data-testid="usage-refresh-date">{(data.plan === 'trial' ? c.trialEnds : c.resets)(when)}</p>}
      {low && (
        <p className="sub__note" role="status" data-testid="usage-low-notice">
          {c.low(c.nouns[low])}{catalog?.eligible ? c.lowMore : ''}
        </p>
      )}
      <ul className="space-y-1 my-3">
        {ROWS.filter((k) => data[k] && (data.unlimited || data[k].allowance > 0)).map((k) => {
          const row = data[k];
          const bal = row.addon ? data.addon_balances?.[row.addon]?.balance || 0 : 0;
          const base = row.plan_allowance ?? row.allowance;
          return (
            <li key={k} className="sub__note" data-testid={`usage-row-${k}`}>
              {c[k]}: <strong>{data.unlimited ? c.unlimited : left(show(k, Math.max(0, base - row.used)), show(k, base))}</strong>
              {!data.unlimited && bal > 0 && <span data-testid={`usage-addon-${k}`}> · {c.extra(show(k, bal))}</span>}
            </li>
          );
        })}
      </ul>
      {catalog?.eligible && (
        <div data-testid="addons-panel">
          <p className="sub__tier">{c.addons}</p>
          <p className="sub__trialnote">{c.addonNote}</p>
          {ios && !items.length && <p className="sub__note" data-testid="addons-ios-pending">{c.ios}</p>}
          {items.map((a) => (
            <button key={a.id} type="button" className="sub__secondary" disabled={!!buying}
              onClick={() => (ios ? buyApple(a) : buyWeb(a.id))} data-testid={`buy-addon-${a.id}`}>
              {c.buy} {a.name[es ? 'es' : 'en']} · {price(a)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembershipUsage;
